import React, { useState } from 'react';
import styled from 'styled-components';

// --- Estilos para el Modal y sus componentes ---

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.65);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.surface};
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: ${props => props.theme.shadows.soft};
  width: 100%;
  max-width: 550px;
  text-align: left;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-family: ${props => props.theme.typography.titleFamily};
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem 0;
  font-size: 1.75rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const ModalSubtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 2rem;
  font-size: 1rem;
  text-align: center;
`;

// Nuevas secciones principales
const PrimarySection = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 12px;
  padding: 1.75rem;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-family: ${props => props.theme.typography.titleFamily};
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HelpText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0.75rem 0 0 0;
  font-style: italic;
`;

// Sección de emergencia colapsable
const EmergencySection = styled.div`
  border: 2px solid #FFA726;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
`;

const EmergencyHeader = styled.div`
  background: linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%);
  padding: 1rem 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: #E65100;
  user-select: none;
  
  &:hover {
    background: linear-gradient(135deg, #FFE0B2 0%, #FFCC80 100%);
  }
`;

const EmergencyContent = styled.div`
  padding: 1.5rem;
  background: #FFFBF7;
  border-top: 1px solid #FFB74D;
`;

const WarningText = styled.p`
  color: #E65100;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
  padding: 0.75rem;
  background: #FFF8E1;
  border-left: 4px solid #FFA726;
  border-radius: 4px;
`;

const ChevronIcon = styled.span`
  transition: transform 0.3s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  font-size: 1.2rem;
`;

// Separador visual
const Separator = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, ${props => props.theme.colors.border} 50%, transparent 100%);
  margin: 1.5rem 0;
`;

// Estilos de botones mejorados
const PrimaryButton = styled.button`
  font-family: ${props => props.theme.typography.fontFamily};
  font-weight: 700;
  font-size: 1.1rem;
  padding: 1rem 2rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, #357ABD 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const DangerButton = styled.button`
  font-family: ${props => props.theme.typography.fontFamily};
  font-weight: 700;
  font-size: 1rem;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  border: 2px solid #FF6B35;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  background: linear-gradient(135deg, #FF6B35 0%, #F7931E 100%);
  color: white;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #E55A2B 0%, #E8831A 100%);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled.button`
  font-family: ${props => props.theme.typography.fontFamily};
  font-weight: 500;
  font-size: 1rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 2rem;
  width: 100%;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.border};
  }
`;


// Componentes de información de estado
const StatusInfo = styled.div`
  background: #F8F9FA;
  border-left: 4px solid ${props => props.theme.colors.primary};
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 2rem;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StatusIcon = styled.span`
  font-size: 1.1rem;
`;

const CierreDiarioModal = ({ isOpen, onClose, onExport, onExecute, isExporting, isExecuting }) => {
    const [showEmergency, setShowEmergency] = useState(false);
    
    if (!isOpen) return null;

    return (
        <ModalBackdrop onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalTitle>
                    🔄 Proceso de Cierre Diario
                </ModalTitle>
                
                <ModalSubtitle>
                    Gestiona el respaldo y cierre diario de las operaciones
                </ModalSubtitle>
                
                <StatusInfo>
                    <StatusItem>
                        <StatusIcon>🕰️</StatusIcon>
                        <span><strong>Cierre automático programado:</strong> Todos los días a las 21:00 hs</span>
                    </StatusItem>
                    <StatusItem>
                        <StatusIcon>💾</StatusIcon>
                        <span><strong>Recordatorio de exportación:</strong> Diariamente a las 18:00 hs</span>
                    </StatusItem>
                    <StatusItem>
                        <StatusIcon>⚡</StatusIcon>
                        <span><strong>Estado actual:</strong> {isExporting ? 'Exportando...' : isExecuting ? 'Ejecutando cierre...' : 'Listo'}</span>
                    </StatusItem>
                </StatusInfo>

                {/* Sección Principal: Exportación */}
                <PrimarySection>
                    <SectionTitle>
                        📤 Exportación a Google Drive
                    </SectionTitle>
                    <p style={{ 
                        margin: '0 0 1.5rem 0', 
                        color: '#495057', 
                        lineHeight: '1.6' 
                    }}>
                        Realiza el respaldo diario de todas las operaciones, cauciones y datos SENEBI 
                        en un archivo Excel en Google Drive.
                    </p>
                    
                    <PrimaryButton onClick={onExport} disabled={isExecuting || isExporting}>
                        {isExporting ? '⏳ Exportando a Drive...' : '📤 Exportar a Drive Ahora'}
                    </PrimaryButton>
                    
                    <HelpText>
                        💡 Se recomienda realizar esta exportación <strong>antes de las 21:00 hs</strong> todos los días hábiles
                    </HelpText>
                </PrimarySection>

                <Separator />

                {/* Sección de Emergencia: Cierre Manual */}
                <EmergencySection>
                    <EmergencyHeader onClick={() => setShowEmergency(!showEmergency)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            ⚠️ <span>Opciones de Emergencia</span>
                        </div>
                        <ChevronIcon expanded={showEmergency}>⌄</ChevronIcon>
                    </EmergencyHeader>
                    
                    {showEmergency && (
                        <EmergencyContent>
                            <WarningText>
                                ⚠️ <strong>ATENCIÓN:</strong> El cierre manual solo debe usarse si el proceso 
                                automático de las 21:00 hs falló o no se ejecutó correctamente.
                            </WarningText>
                            
                            <p style={{ 
                                margin: '0 0 1.5rem 0', 
                                color: '#6C757D', 
                                fontSize: '0.9rem',
                                lineHeight: '1.5'
                            }}>
                                Este proceso es <strong>irreversible</strong> y limpiará todas las operaciones 
                                del día para preparar la planilla para el próximo día hábil.
                            </p>
                            
                            <DangerButton onClick={onExecute} disabled={isExecuting || isExporting}>
                                {isExecuting ? '⏳ Ejecutando Cierre...' : '🔧 Ejecutar Cierre Manual'}
                            </DangerButton>
                        </EmergencyContent>
                    )}
                </EmergencySection>

                <CancelButton onClick={onClose} disabled={isExecuting || isExporting}>
                    Cerrar
                </CancelButton>
            </ModalContent>
        </ModalBackdrop>
    );
};

export default CierreDiarioModal;